import type { SeriesItem, VodItem } from "@hybrid-iptv/types";
import { parseCatalogTitle } from "../home/catalogTitleMatch.js";
import { normalize, scoreMatch } from "../search/useUniversalSearch.js";
import { isShelfCandidate, pickShelfFill } from "../home/shelfFill.js";
import { dupKeyOf, duplicateFlags, streamQuality } from "./duplicates.js";
import type { ShelfCatalogItem } from "../home/shelfFill.js";

/**
 * One definition of a catalog query (Movies, Series and, next, Search, My List
 * and Home), answered two ways with the same result:
 *  - in the APK, by native SQLite over the normalised catalog
 *    (native/.../catalog/CatalogStore.query), so a TV never holds 50,000+ titles
 *    on the page (stage B, owner 2026-09-19);
 *  - on the website, and wherever the native store is missing, by queryCatalog
 *    below over the in-memory catalog the server sends.
 * The keys the native side filters and matches on are computed HERE, with the
 * page's own functions, and stored beside each item (catalogRowKeys).
 */

export type CatalogKind = "vod" | "series";
/** "popular": most viewed first, from the device's discovery files (Section 26); elsewhere the provider's order. */
export type CatalogSort = "az" | "year" | "pos" | "popular";
export type CatalogItem = VodItem | SeriesItem;

export interface CatalogQuery {
  kind: CatalogKind;
  /** Exact categoryId. */
  category?: string;
  /** Only these item ids (favourites, My List). */
  ids?: string[];
  /** Only these provider TMDB ids (Home rails). */
  tmdbIds?: number[];
  /** Only these parseCatalogTitle keys, for items without a TMDB id (Home rails). */
  titleKeys?: string[];
  /** Every stream of one title (duplicates.ts dupKeyOf), best first, none hidden: the fallbacks. */
  dupKey?: string;
  /** Case-insensitive substring of rawTitle: the Movies/Series search box. */
  text?: string;
  /** The Search screen: normalize()d needle, scored by scoreMatch, best first. */
  search?: string;
  /** Ignored when `search` is set. Default "pos" (the provider's order). */
  sort?: CatalogSort;
  offset?: number;
  limit: number;
}

export interface CatalogPage<T extends CatalogItem = CatalogItem> {
  total: number;
  items: T[];
}

/** The keys stored natively with each item, computed with the page's own functions. */
export function catalogRowKeys(item: CatalogItem) {
  return {
    titleLc: item.rawTitle.toLowerCase(),
    searchKey: normalize(item.rawTitle),
    // buildTitleIndex only indexes titles the provider gave no TMDB id.
    titleKey: item.tmdbId && item.tmdbId > 0 ? null : parseCatalogTitle(item.rawTitle).key || null,
    // Home's genre-row top-up (shelfFill.ts) filters and orders on these.
    shelfOk: isShelfCandidate(item),
    rating: item.rating ?? null,
    // One entry per title in lists (duplicates.ts): which title this stream is, and how good it looks.
    dupKey: dupKeyOf(item),
    quality: streamQuality(item),
  };
}

/** A stable sort that keeps the provider's order among equals, as Array#sort does. */
function sorted<T>(list: T[], compare: (a: T, b: T) => number): T[] {
  return [...list].sort(compare);
}

/**
 * The query over an in-memory list (already parental-filtered). This is the
 * reference the native SQL must match.
 */
export function queryCatalog<T extends CatalogItem>(items: readonly T[], query: CatalogQuery): CatalogPage<T> {
  const ids = query.ids ? new Set(query.ids) : null;
  const tmdbIds = query.tmdbIds ? new Set(query.tmdbIds) : null;
  const titleKeys = query.titleKeys ? new Set(query.titleKeys) : null;
  const text = query.text ? query.text.toLowerCase() : "";
  // One entry per title (owner, 2026-10-13): a lookup by id and the fallbacks of one title see
  // every copy; every other list sees the best stream of each (within the category, in a category).
  const hidden = query.ids || query.dupKey ? null : query.category !== undefined ? duplicateFlags(items).hiddenCat : duplicateFlags(items).hiddenAll;
  let list = items.filter((item) => {
    if (query.category !== undefined && item.categoryId !== query.category) return false;
    if (hidden && hidden.has(item.id)) return false;
    if (query.dupKey !== undefined && dupKeyOf(item) !== query.dupKey) return false;
    if (ids && !ids.has(item.id)) return false;
    if (tmdbIds && !(item.tmdbId !== undefined && tmdbIds.has(item.tmdbId))) return false;
    if (titleKeys) {
      const key = catalogRowKeys(item).titleKey;
      if (!key || !titleKeys.has(key)) return false;
    }
    if (text && !item.rawTitle.toLowerCase().includes(text)) return false;
    return true;
  });
  if (query.dupKey !== undefined) {
    // The fallbacks of one title, best stream first.
    const quality = new Map(list.map((item) => [item.id, streamQuality(item)] as const));
    list = sorted(list, (a, b) => (quality.get(b.id) ?? 0) - (quality.get(a.id) ?? 0));
  } else if (query.search) {
    const needle = query.search;
    const scored = list
      .map((item) => ({ item, score: scoreMatch(normalize(item.rawTitle), needle) }))
      .filter((hit) => hit.score > 0);
    scored.sort((a, b) => b.score - a.score || a.item.rawTitle.localeCompare(b.item.rawTitle));
    list = scored.map((hit) => hit.item);
  } else if (query.sort === "az") {
    list = sorted(list, (a, b) => a.rawTitle.localeCompare(b.rawTitle));
  } else if (query.sort === "year") {
    list = sorted(list, (a, b) => (("year" in b ? b.year : undefined) ?? 0) - (("year" in a ? a.year : undefined) ?? 0));
  }
  const offset = query.offset ?? 0;
  return { total: list.length, items: list.slice(offset, offset + query.limit) };
}

/** Home's genre-row top-up: pickShelfFill over one row's candidates. */
export interface ShelfFillRequest {
  kind: CatalogKind;
  /** The categories whose names fit the row (shelvesForCategory). */
  categories: string[];
  /** TMDB ids already on Home. */
  used: number[];
  count: number;
  seed: string;
}

/** The in-memory answer; native (CatalogStore.shelfFill) must match it. */
export function shelfFillFromItems<T extends CatalogItem>(items: readonly T[], request: ShelfFillRequest): T[] {
  const categories = new Set(request.categories);
  const candidates = items.filter(
    (item) => item.categoryId !== undefined && categories.has(item.categoryId) && isShelfCandidate(item),
  ) as unknown as ShelfCatalogItem[];
  return pickShelfFill(candidates, new Set(request.used), request.count, request.seed) as unknown as T[];
}

/** Distinct categories with counts, sorted as the pages always sorted them. */
export function catalogCategories(items: readonly CatalogItem[]): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const item of items) if (item.categoryId) counts.set(item.categoryId, (counts.get(item.categoryId) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
}
